import { GameMode } from './ruleset';

export const LIMITS = {
  maxEventsPerBatch: 512,
  maxEventRequestBytes: 64 * 1024,
  maxEventsPerRun: 2_000_000,
  maxTranscriptBytes: 32 * 1024 * 1024,
  maxRunSeconds: 6 * 60 * 60,
  maxPauseSeconds: 30 * 60,
  runStartsPerIpPerMinute: 10,
  eventRequestsPerRunPerMinute: 120,
  submissionsPerIpPerMinute: 3,
  maxLeaderboardRows: 100,
} as const;

export type Transition = 'none' | 'pause' | 'resume';

export interface InputEvent {
  sequence: number;
  tick: number;
  playerSlot: 1 | 2;
  inputBits: number;
  transition: Transition;
}

export interface StartRunBody {
  mode: GameMode;
}

export interface SubmitBody {
  displayName: string;
}

export interface ValidationResult<T> {
  ok: boolean;
  value?: T;
  error?: string;
}

const EVENT_KEYS = ['sequence', 'tick', 'playerSlot', 'inputBits', 'transition'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function exactKeys(value: Record<string, unknown>, allowed: readonly string[]): boolean {
  return Object.keys(value).every((key) => allowed.includes(key)) && allowed.every((key) => key in value);
}

export function normalizeDisplayName(value: unknown): ValidationResult<string> {
  if (typeof value !== 'string') return { ok: false, error: 'invalid display name' };
  const name = value.trim().toUpperCase();
  if (name.length < 1 || name.length > 16 || !/^[A-Z0-9 _-]+$/.test(name)) return { ok: false, error: 'invalid display name' };
  return { ok: true, value: name };
}

export function validateStartBody(body: unknown): ValidationResult<StartRunBody> {
  if (!isRecord(body) || !exactKeys(body, ['mode'])) return { ok: false, error: 'invalid run request' };
  if (body.mode !== 'single-player' && body.mode !== 'alternating-two-player') return { ok: false, error: 'invalid run mode' };
  return { ok: true, value: { mode: body.mode as GameMode } };
}

export function validateSubmitBody(body: unknown): ValidationResult<SubmitBody> {
  if (!isRecord(body) || !exactKeys(body, ['displayName'])) return { ok: false, error: 'invalid submission' };
  const normalized = normalizeDisplayName(body.displayName);
  return normalized.ok ? { ok: true, value: { displayName: normalized.value ?? '' } } : { ok: false, error: normalized.error ?? 'invalid display name' };
}

export function validateInputEvent(body: unknown, expected: { sequence: number; tick: number; playerSlot: 1 | 2; activePlayer: 1 | 2; totalEvents: number; transcriptBytes: number }): ValidationResult<InputEvent> {
  if (!isRecord(body) || !exactKeys(body, EVENT_KEYS)) return { ok: false, error: 'invalid event schema' };
  const event = body as { sequence: unknown; tick: unknown; playerSlot: unknown; inputBits: unknown; transition: unknown };
  if (!Number.isSafeInteger(event.sequence) || event.sequence !== expected.sequence + 1) return { ok: false, error: 'invalid event sequence' };
  if (typeof event.tick !== 'number' || !Number.isSafeInteger(event.tick) || event.tick <= expected.tick || event.tick < 0) return { ok: false, error: 'invalid simulation tick' };
  if (event.playerSlot !== 1 && event.playerSlot !== 2) return { ok: false, error: 'invalid player slot' };
  if (event.playerSlot !== expected.playerSlot || event.playerSlot !== expected.activePlayer) return { ok: false, error: 'inactive player' };
  if (typeof event.inputBits !== 'number' || !Number.isInteger(event.inputBits) || event.inputBits < 0 || (event.inputBits & ~7) !== 0 || (event.inputBits & 3) === 3) return { ok: false, error: 'invalid input bits' };
  if (event.transition !== 'none' && event.transition !== 'pause' && event.transition !== 'resume') return { ok: false, error: 'invalid transition' };
  if (event.transition !== 'none' && event.inputBits !== 0) return { ok: false, error: 'transition must have no input' };
  if (expected.totalEvents >= LIMITS.maxEventsPerRun) return { ok: false, error: 'transcript limit exceeded' };
  return { ok: true, value: { sequence: event.sequence as number, tick: event.tick as number, playerSlot: event.playerSlot as 1 | 2, inputBits: event.inputBits as number, transition: event.transition as Transition } };
}

export function validateEventBatch(body: unknown, expected: { sequence: number; tick: number; playerSlot: 1 | 2; activePlayer: 1 | 2; totalEvents: number; transcriptBytes: number }, encodedBytes: number): ValidationResult<InputEvent[]> {
  if (encodedBytes > LIMITS.maxEventRequestBytes) return { ok: false, error: 'event request too large' };
  if (!Array.isArray(body) || body.length === 0 || body.length > LIMITS.maxEventsPerBatch) return { ok: false, error: 'invalid event batch' };
  const events: InputEvent[] = [];
  let sequence = expected.sequence;
  let tick = expected.tick;
  for (const item of body) {
    const result = validateInputEvent(item, { ...expected, sequence, tick, totalEvents: expected.totalEvents + events.length });
    if (!result.ok || !result.value) return { ok: false, error: result.error ?? 'invalid event' };
    events.push(result.value);
    sequence = result.value.sequence;
    tick = result.value.tick;
  }
  const nextBytes = expected.transcriptBytes + encodedBytes;
  if (nextBytes > LIMITS.maxTranscriptBytes) return { ok: false, error: 'transcript limit exceeded' };
  return { ok: true, value: events };
}

export function encodeCursor(offset: number): string {
  return `c1.${toBase64Url(String(Math.max(0, Math.floor(offset))))}`;
}

export function decodeCursor(cursor: string | null): number {
  if (!cursor || !cursor.startsWith('c1.')) return 0;
  try {
    const decoded = fromBase64Url(cursor.slice(3));
    const offset = Number(decoded);
    return Number.isSafeInteger(offset) && offset >= 0 ? offset : 0;
  } catch {
    return 0;
  }
}

function toBase64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function fromBase64Url(value: string): string {
  const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((value.length + 3) % 4));
  return new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)));
}
